import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import type { getControllerConfigLatestVersionParams } from '@/features/controller/configuration/endpoints/controllerConfiguration'
import {
  GET_LATEST_CONFIG_VERSION_API,
  getControllerConfigLatestVersion,
} from '@/features/controller/configuration/endpoints/controllerConfiguration'
import type { ControllerConfigVersion } from '@/features/controller/configuration/types'
import type { UseQueryOptions } from '@/types/queries'

export type UseControllerConfigLatestVersionQueryResult = Omit<UseQueryResult, 'data'> & {
  latestConfigVersion: ControllerConfigVersion | null
}

function latestControllerConfigurationQueryKey(params: getControllerConfigLatestVersionParams) {
  return [GET_LATEST_CONFIG_VERSION_API, params]
}

export function useLatestControllerConfiguration(
  params: getControllerConfigLatestVersionParams,
  options?: UseQueryOptions<ControllerConfigVersion | null>,
): UseControllerConfigLatestVersionQueryResult {
  const { data, ...queryResult } = useQuery<ControllerConfigVersion | null>({
    ...options,
    queryFn: () => getControllerConfigLatestVersion(params),
    queryKey: latestControllerConfigurationQueryKey(params),
  })

  return {
    ...queryResult,
    latestConfigVersion: data || null,
  }
}

/** Fetches the latest version even if the cached one is fresh, and caches what it gets */
export function useFetchLatestControllerConfiguration() {
  const queryClient = useQueryClient()

  return (params: getControllerConfigLatestVersionParams) =>
    queryClient.fetchQuery({
      queryFn: () => getControllerConfigLatestVersion(params),
      queryKey: latestControllerConfigurationQueryKey(params),
      staleTime: 0,
    })
}

export function useInvalidateLatestControllerConfiguration() {
  const queryClient = useQueryClient()

  return (params: getControllerConfigLatestVersionParams) =>
    queryClient.invalidateQueries({ queryKey: latestControllerConfigurationQueryKey(params) })
}
